/**
 * EO-03 — Event 360 Operational Command View
 *
 * Architecture: NEEDS ATTENTION | EVENT FLOW | ACT NOW
 * Deterministic priorities rendered immediately.
 * AI narrative loads asynchronously — never blocks.
 * Drill-through to authoritative sources for every item.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/services/api";
import {
  ArrowLeft, RefreshCw, Target, AlertTriangle, CheckCircle2,
  Clock, Activity, GitPullRequest, Users, Calendar, MapPin,
  ChevronRight, Loader2, Zap, Shield, AlertCircle, TrendingUp,
  MessageSquare, CheckSquare, ThumbsUp, XCircle, Eye, Layers,
  Network, Sparkles
} from "lucide-react";
import { toast } from "sonner";

// ─── Priority colours ─────────────────────────────────────────────────────────
const PRIORITY = {
  CRITICAL: { bg: "bg-[#FCEBEC]", text: "text-[#BA0517]", border: "border-[#BA0517]/20", dot: "bg-[#BA0517]", label: "CRITICAL" },
  HIGH:     { bg: "bg-[#FFF4E5]", text: "text-[#8C4B00]", border: "border-[#FE9339]/30", dot: "bg-[#FE9339]", label: "HIGH" },
  MEDIUM:   { bg: "bg-[#EEF4FF]", text: "text-[#0176D3]", border: "border-[#0176D3]/20", dot: "bg-[#0176D3]", label: "MEDIUM" },
  LOW:      { bg: "bg-[#F3F3F3]", text: "text-[#444444]", border: "border-[#C9C9C9]",    dot: "bg-[#706E6B]", label: "LOW" },
  INFO:     { bg: "bg-[#EAF5EE]", text: "text-[#2E844A]", border: "border-[#2E844A]/20", dot: "bg-[#2E844A]", label: "INFO" },
};

// ─── Thread health colours ────────────────────────────────────────────────────
const HEALTH = {
  BLOCKED:   { label: "BLOCKED",    dot: "bg-[#BA0517]", text: "text-[#BA0517]",  bg: "bg-[#FCEBEC]" },
  AT_RISK:   { label: "AT RISK",    dot: "bg-[#FE9339]", text: "text-[#8C4B00]",  bg: "bg-[#FFF4E5]" },
  ATTENTION: { label: "ATTENTION",  dot: "bg-[#0176D3]", text: "text-[#0176D3]",  bg: "bg-[#EEF4FF]" },
  ON_TRACK:  { label: "ON TRACK",   dot: "bg-[#2E844A]", text: "text-[#2E844A]",  bg: "bg-[#EAF5EE]" },
  READY:     { label: "READY",      dot: "bg-[#2E844A]", text: "text-[#2E844A]",  bg: "bg-[#EAF5EE]" },
  ACTIVE:    { label: "ACTIVE",     dot: "bg-[#0176D3]", text: "text-[#0176D3]",  bg: "bg-[#EEF4FF]" },
  COMPLETED: { label: "COMPLETED",  dot: "bg-[#706E6B]", text: "text-[#706E6B]",  bg: "bg-[#F3F3F3]" },
  CLOSED:    { label: "CLOSED",     dot: "bg-[#706E6B]", text: "text-[#706E6B]",  bg: "bg-[#F3F3F3]" },
};

// ─── Readiness state colours ──────────────────────────────────────────────────
const READINESS = {
  READY:     { bg: "bg-[#EAF5EE]", text: "text-[#2E844A]", border: "border-[#2E844A]/20", icon: <CheckCircle2 className="w-4 h-4" /> },
  ATTENTION: { bg: "bg-[#FFF4E5]", text: "text-[#8C4B00]", border: "border-[#FE9339]/30", icon: <AlertTriangle className="w-4 h-4" /> },
  BLOCKED:   { bg: "bg-[#FCEBEC]", text: "text-[#BA0517]", border: "border-[#BA0517]/20", icon: <XCircle className="w-4 h-4" /> },
  AT_RISK:   { bg: "bg-[#FFF4E5]", text: "text-[#8C4B00]", border: "border-[#FE9339]/30", icon: <AlertCircle className="w-4 h-4" /> },
  NOT_READY: { bg: "bg-[#F3F3F3]", text: "text-[#444444]", border: "border-[#C9C9C9]",    icon: <Clock className="w-4 h-4" /> },
  UNKNOWN:   { bg: "bg-[#F3F3F3]", text: "text-[#706E6B]", border: "border-[#C9C9C9]",    icon: <Loader2 className="w-4 h-4 animate-spin" /> },
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}

function formatDateDelta(dateStr) {
  const days = daysUntil(dateStr);
  if (days === null) return "";
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `in ${days}d`;
}

// ─── Shared sub-components ────────────────────────────────────────────────────
function SectionHeader({ icon, title, count, countColor = "text-[#0176D3]" }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <span className="text-[#706E6B]">{icon}</span>
      <h3 className="text-sm font-semibold text-[#181818]" style={{ fontFamily: "'Outfit', sans-serif" }}>
        {title}
      </h3>
      {count !== undefined && count !== null && (
        <span className={`text-xs font-mono px-2 py-0.5 rounded-full bg-[#F3F3F3] ${countColor}`}>
          {count}
        </span>
      )}
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="flex items-center gap-2 py-3 px-1">
      <CheckCircle2 className="w-4 h-4 text-[#2E844A]" />
      <span className="text-xs text-[#706E6B]">{message}</span>
    </div>
  );
}

function DrillButton({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1 text-xs text-[#0176D3] hover:underline flex-shrink-0"
    >
      {children}
      <ChevronRight className="w-3 h-3" />
    </button>
  );
}

// ─── SECTION 1: NEEDS ATTENTION ───────────────────────────────────────────────
function NeedsAttention({ data, navigate }) {
  const { attention } = data;
  const hasAnyIssue =
    (attention.blockers?.length || 0) > 0 ||
    (attention.overdue_milestones?.length || 0) > 0 ||
    (attention.critical_issues?.length || 0) > 0 ||
    (attention.waiting_approvals?.length || 0) > 0 ||
    (attention.open_decisions?.length || 0) > 0;

  const eventId = data.event_id;

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-5" data-testid="eo03-needs-attention">
      <SectionHeader icon={<AlertTriangle className="w-4 h-4" />} title="NEEDS ATTENTION" />

      {!hasAnyIssue && (
        <div className="flex items-center gap-2 py-4">
          <CheckCircle2 className="w-5 h-5 text-[#2E844A]" />
          <span className="text-sm font-medium text-[#2E844A]">Nothing requires immediate attention</span>
        </div>
      )}

      {/* Blockers */}
      {attention.blockers?.length > 0 && (
        <AttentionGroup
          icon={<XCircle className="w-3.5 h-3.5 text-[#BA0517]" />}
          label="Blockers"
          items={attention.blockers}
          colorClass="text-[#BA0517]"
          renderItem={m => (
            <span className="flex-1 text-xs text-[#181818] truncate">{m.title || m.id}</span>
          )}
        />
      )}

      {/* Overdue milestones */}
      {attention.overdue_milestones?.length > 0 && (
        <AttentionGroup
          icon={<Clock className="w-3.5 h-3.5 text-[#8C4B00]" />}
          label="Overdue Milestones"
          items={attention.overdue_milestones}
          colorClass="text-[#8C4B00]"
          renderItem={m => (
            <>
              <span className="flex-1 text-xs text-[#181818] truncate">{m.title}</span>
              <span className="text-[10px] text-[#BA0517] flex-shrink-0 font-medium">
                {formatDateDelta(m.due_at)}
              </span>
            </>
          )}
        />
      )}

      {/* Critical issues */}
      {attention.critical_issues?.length > 0 && (
        <AttentionGroup
          icon={<AlertTriangle className="w-3.5 h-3.5 text-[#BA0517]" />}
          label="Critical Issues"
          items={attention.critical_issues}
          colorClass="text-[#BA0517]"
          renderItem={i => (
            <>
              <span className="flex-1 text-xs text-[#181818] truncate">{i.title}</span>
              <span className="text-[10px] text-[#BA0517] flex-shrink-0 font-medium uppercase">{i.severity}</span>
            </>
          )}
          onViewAll={() => navigate(`/imagine/events/${eventId}?tab=issues`)}
        />
      )}

      {/* Waiting approvals */}
      {attention.waiting_approvals?.length > 0 && (
        <AttentionGroup
          icon={<ThumbsUp className="w-3.5 h-3.5 text-[#8C4B00]" />}
          label="Waiting Approvals"
          items={attention.waiting_approvals}
          colorClass="text-[#8C4B00]"
          renderItem={a => (
            <>
              <span className="flex-1 text-xs text-[#181818] truncate">{a.title}</span>
              {a.required && (
                <span className="text-[10px] text-[#BA0517] font-medium flex-shrink-0">Required</span>
              )}
            </>
          )}
        />
      )}

      {/* Open decisions */}
      {attention.open_decisions?.length > 0 && (
        <AttentionGroup
          icon={<MessageSquare className="w-3.5 h-3.5 text-[#0176D3]" />}
          label="Open Decisions"
          items={attention.open_decisions}
          colorClass="text-[#0176D3]"
          renderItem={d => (
            <span className="flex-1 text-xs text-[#181818] truncate">{d.title}</span>
          )}
        />
      )}
    </div>
  );
}

function AttentionGroup({ icon, label, items, colorClass, renderItem, onViewAll }) {
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          {icon}
          <span className={`text-[10px] font-semibold uppercase tracking-wide ${colorClass}`}>{label}</span>
          <span className="text-[10px] text-[#706E6B]">({items.length})</span>
        </div>
        {onViewAll && (
          <button onClick={onViewAll} className="text-[10px] text-[#0176D3] hover:underline">
            View all
          </button>
        )}
      </div>
      <div className="space-y-1">
        {items.slice(0, 3).map((item, i) => (
          <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 bg-[#FAFAFA] rounded-lg">
            {renderItem(item)}
          </div>
        ))}
        {items.length > 3 && (
          <p className="text-[10px] text-[#706E6B] pl-2.5">+{items.length - 3} more</p>
        )}
      </div>
    </div>
  );
}

// ─── SECTION 2: EVENT FLOW ────────────────────────────────────────────────────
function EventFlow({ data, navigate }) {
  const { event_flow, threads, event_id: eventId } = data;
  const readiness = event_flow?.readiness || data.readiness || {};
  const rs = READINESS[readiness.overall_state] || READINESS.UNKNOWN;

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-5" data-testid="eo03-event-flow">
      <SectionHeader icon={<TrendingUp className="w-4 h-4" />} title="EVENT FLOW" />

      <div className="space-y-4">
        {/* Readiness */}
        <div className={`flex items-center justify-between p-3 rounded-lg ${rs.bg} border ${rs.border}`}>
          <div className="flex items-center gap-2">
            <span className={rs.text}>{rs.icon}</span>
            <div>
              <p className={`text-xs font-semibold ${rs.text}`}>
                Readiness: {readiness.overall_state?.replace(/_/g, " ") || "UNKNOWN"}
              </p>
              <p className="text-[10px] text-[#706E6B]">{readiness.overall_pct ?? 0}% of gates satisfied</p>
            </div>
          </div>
          <DrillButton onClick={() => navigate(`/events/${eventId}/readiness`)}>
            Details
          </DrillButton>
        </div>

        {/* Critical path */}
        <FlowRow
          icon={<Layers className="w-3.5 h-3.5 text-[#0176D3]" />}
          label="Critical Path"
          value={`${event_flow?.critical_path?.completed_count ?? 0}/${event_flow?.critical_path?.total_count ?? 0} milestones`}
          sub={event_flow?.next_milestone
            ? `Next: ${event_flow.next_milestone.title || "milestone"} (${formatDateDelta(event_flow.next_milestone.due_at)})`
            : "All milestones completed"}
          alert={(event_flow?.critical_path?.blocked_count ?? 0) > 0 || (event_flow?.critical_path?.overdue_count ?? 0) > 0}
          alertText={[
            event_flow?.critical_path?.blocked_count > 0 ? `${event_flow.critical_path.blocked_count} blocked` : null,
            event_flow?.critical_path?.overdue_count > 0 ? `${event_flow.critical_path.overdue_count} overdue` : null,
          ].filter(Boolean).join(" · ")}
          onClick={() => navigate(`/events/${eventId}/readiness`)}
        />

        {/* Changes */}
        <FlowRow
          icon={<GitPullRequest className="w-3.5 h-3.5 text-[#0176D3]" />}
          label="Changes"
          value={`${event_flow?.open_changes_count ?? 0} open`}
          alert={(event_flow?.open_changes_count ?? 0) > 0}
          alertText={`${event_flow?.open_changes_count} unresolved change${event_flow?.open_changes_count !== 1 ? "s" : ""}`}
          onClick={() => navigate(`/imagine/events/${eventId}?tab=changes`)}
        />

        {/* Thread risk */}
        {(event_flow?.thread_risk?.length ?? 0) > 0 ? (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <Network className="w-3.5 h-3.5 text-[#BA0517]" />
                <span className="text-xs font-medium text-[#444444]">Thread Risk</span>
              </div>
              <DrillButton onClick={() => navigate(`/events/${eventId}/threads`)}>All threads</DrillButton>
            </div>
            <div className="space-y-1">
              {event_flow.thread_risk.slice(0, 4).map((t, i) => {
                const h = HEALTH[t.health] || HEALTH.ACTIVE;
                return (
                  <button
                    key={i}
                    onClick={() => navigate(`/events/${eventId}/threads`)}
                    className="w-full flex items-center gap-2.5 p-2 bg-[#FAFAFA] rounded-lg hover:bg-[#F3F3F3] transition-colors"
                  >
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${h.dot}`} />
                    <span className="flex-1 text-xs text-[#181818] text-left truncate">{t.name}</span>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${h.bg} ${h.text}`}>
                      {h.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <FlowRow
            icon={<Network className="w-3.5 h-3.5 text-[#2E844A]" />}
            label="Thread Risk"
            value={`${threads?.length ?? 0} threads`}
            sub="No threads blocked or at risk"
            onClick={() => navigate(`/events/${eventId}/threads`)}
          />
        )}
      </div>
    </div>
  );
}

function FlowRow({ icon, label, value, sub, alert, alertText, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-[#F3F3F3] transition-colors text-left group"
    >
      <span className="flex-shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-[#444444]">{label}</span>
          {alert && alertText && (
            <span className="text-[10px] text-[#BA0517] font-medium">{alertText}</span>
          )}
        </div>
        {sub && <p className="text-[10px] text-[#706E6B] truncate mt-0.5">{sub}</p>}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <span className="text-[11px] text-[#706E6B] font-mono">{value}</span>
        <ChevronRight className="w-3 h-3 text-[#C9C9C9] group-hover:text-[#706E6B] transition-colors" />
      </div>
    </button>
  );
}

// ─── SECTION 3: ACT NOW ───────────────────────────────────────────────────────
function ActNow({ data, copilot, loadingCopilot, navigate }) {
  const { next_actions, event_id: eventId } = data;

  function handleAction(action) {
    const t = action.action_type;
    if (t === "OPEN_ISSUE") navigate(`/imagine/events/${eventId}?tab=issues`);
    else if (t === "OPEN_APPROVAL") navigate(`/imagine/events/${eventId}?tab=approvals`);
    else if (t === "OPEN_DECISION") navigate(`/imagine/events/${eventId}?tab=decisions`);
    else if (t === "OPEN_CHANGE") navigate(`/events/${eventId}/changes/${action.action_target}`);
    else if (t === "REVIEW_MILESTONE") navigate(`/events/${eventId}/readiness`);
    else navigate(`/imagine/events/${eventId}`);
  }

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-5" data-testid="eo03-act-now">
      <SectionHeader icon={<Zap className="w-4 h-4" />} title="ACT NOW" count={next_actions?.length} />

      {/* Deterministic priorities — loads immediately */}
      {!next_actions?.length && (
        <EmptyState message="No immediate actions required" />
      )}

      <div className="space-y-2 mb-4">
        {(next_actions || []).slice(0, 5).map((action, i) => {
          const p = PRIORITY[action.priority] || PRIORITY.LOW;
          return (
            <button
              key={i}
              data-testid={`action-item-${i}`}
              onClick={() => handleAction(action)}
              className={`w-full text-left p-3 rounded-xl border ${p.border} ${p.bg} hover:brightness-95 transition-all`}
            >
              <div className="flex items-start gap-2.5">
                <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                  <span className="text-[10px] font-bold text-[#706E6B] font-mono w-3 text-right">{action.rank}</span>
                  <div className={`w-2 h-2 rounded-full ${p.dot}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-[10px] font-semibold uppercase tracking-wide ${p.text}`}>
                      {p.label}
                    </span>
                    <span className="text-[10px] text-[#706E6B] truncate">{action.source_type?.replace(/_/g, " ")}</span>
                  </div>
                  <p className="text-xs font-semibold text-[#181818] leading-tight">{action.title}</p>
                  {action.reason && (
                    <p className="text-[10px] text-[#706E6B] mt-0.5 leading-tight line-clamp-2">{action.reason}</p>
                  )}
                  {action.blocks?.length > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-[9px] text-[#706E6B]">Blocks:</span>
                      {action.blocks.slice(0, 2).map((b, j) => (
                        <span key={j} className="text-[9px] bg-[#F3F3F3] text-[#444444] px-1.5 py-0.5 rounded-full">
                          {b}
                        </span>
                      ))}
                      {action.blocks.length > 2 && (
                        <span className="text-[9px] text-[#706E6B]">+{action.blocks.length - 2}</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
                  {action.owner && action.owner !== "Unassigned" && (
                    <span className="text-[9px] text-[#706E6B] max-w-[80px] truncate">{action.owner}</span>
                  )}
                  <ChevronRight className="w-3 h-3 text-[#C9C9C9]" />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* AI narrative — loads async, never blocks */}
      <div className="border-t border-[#E5E5E5] pt-3 mt-1">
        <div className="flex items-center gap-1.5 mb-2">
          <Sparkles className="w-3 h-3 text-[#5E4DB2]" />
          <span className="text-[10px] font-semibold text-[#5E4DB2] uppercase tracking-wide">Planning Brief</span>
          {loadingCopilot && <Loader2 className="w-2.5 h-2.5 animate-spin text-[#5E4DB2]" />}
        </div>
        {copilot ? (
          <p className="text-xs text-[#444444] leading-relaxed">{copilot.summary}</p>
        ) : (
          <p className="text-[10px] text-[#706E6B]">
            {loadingCopilot ? "Generating brief…" : "AI brief unavailable"}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Thread Health Grid ───────────────────────────────────────────────────────
function ThreadGrid({ threads, eventId, navigate }) {
  if (!threads?.length) return null;
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-5" data-testid="eo03-thread-grid">
      <div className="flex items-center justify-between mb-4">
        <SectionHeader icon={<Network className="w-4 h-4" />} title="THREAD HEALTH" count={threads.length} />
        <DrillButton onClick={() => navigate(`/events/${eventId}/threads`)}>Open Threads</DrillButton>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {threads.map((t, i) => {
          const h = HEALTH[t.health || t.status] || HEALTH.ACTIVE;
          return (
            <button
              key={i}
              data-testid={`thread-health-${i}`}
              onClick={() => navigate(`/events/${eventId}/threads`)}
              className="flex items-center gap-2.5 p-2.5 bg-[#FAFAFA] rounded-lg hover:bg-[#F3F3F3] transition-colors text-left"
            >
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${h.dot}`} />
              <span className="flex-1 text-xs text-[#181818] truncate">{t.name}</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {t.open_critical_issues > 0 && (
                  <span className="text-[9px] text-[#BA0517] font-bold">{t.open_critical_issues}C</span>
                )}
                {t.open_issues > 0 && (
                  <span className="text-[9px] text-[#BA0517]">{t.open_issues}I</span>
                )}
                {t.pending_approvals > 0 && (
                  <span className="text-[9px] text-[#8C4B00]">{t.pending_approvals}A</span>
                )}
                <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${h.bg} ${h.text}`}>
                  {h.label}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Counter Strip ────────────────────────────────────────────────────────────
function CounterStrip({ data }) {
  const counters = [
    { label: "Open Issues",    value: data.total_open_issues,      color: "text-[#BA0517]" },
    { label: "Approvals",      value: data.total_pending_approvals, color: "text-[#8C4B00]" },
    { label: "Open Decisions", value: data.total_open_decisions,    color: "text-[#0176D3]" },
    { label: "Overdue",        value: data.total_overdue_milestones, color: data.total_overdue_milestones > 0 ? "text-[#BA0517]" : "text-[#2E844A]" },
  ];
  return (
    <div className="grid grid-cols-4 gap-2">
      {counters.map((c, i) => (
        <div key={i} className="bg-white border border-[#E5E5E5] rounded-xl p-3 text-center">
          <p className={`text-xl font-bold ${c.color}`}>{c.value ?? 0}</p>
          <p className="text-[10px] text-[#706E6B] mt-0.5">{c.label}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Event360() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [copilot, setCopilot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingCopilot, setLoadingCopilot] = useState(false);
  const [pageError, setPageError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const r = await api.get(`/events/${eventId}/360`);
      setData(r.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) setPageError("not_found");
      else if (status === 403) setPageError("forbidden");
      else setPageError("load_error");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  const loadCopilot = useCallback(() => {
    setLoadingCopilot(true);
    setCopilot(null);
    api.get(`/events/${eventId}/copilot`)
      .then(r => setCopilot(r.data))
      .catch(() => {})
      .finally(() => setLoadingCopilot(false));
  }, [eventId]);

  useEffect(() => {
    load();
    loadCopilot();
  }, [load, loadCopilot]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Loader2 className="w-7 h-7 animate-spin text-[#0176D3]" />
        <p className="text-sm text-[#706E6B]">Loading Event 360…</p>
      </div>
    );
  }

  if (pageError) {
    const msgs = {
      not_found:  { title: "Event Not Found",   body: "This event does not exist or you do not have access." },
      forbidden:  { title: "Access Denied",     body: "You do not have permission to view this Event 360." },
      load_error: { title: "Load Error",        body: "Failed to load Event 360. Please retry." },
    };
    const m = msgs[pageError] || msgs.load_error;
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4 text-center p-8">
        <AlertTriangle className="w-10 h-10 text-[#706E6B]" />
        <div>
          <p className="text-sm font-semibold text-[#444444]">{m.title}</p>
          <p className="text-xs text-[#706E6B] mt-1">{m.body}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="px-4 py-2 text-xs bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/30 rounded-lg hover:bg-[#253550] transition-colors">Retry</button>
          <button onClick={() => navigate("/events")} className="px-4 py-2 text-xs bg-[#FAFAFA] text-[#706E6B] border border-[#C9C9C9] rounded-lg">Back to Events</button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const readiness = data.event_flow?.readiness || data.readiness || {};
  const rs = READINESS[readiness.overall_state] || READINESS.UNKNOWN;
  const days = daysUntil(data.event_date);

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-5" data-testid="event-360-container">

      {/* ── Page header ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(`/imagine/events/${eventId}`)} className="p-2 hover:bg-[#FAFAFA] rounded-lg text-[#706E6B]" data-testid="360-back-btn">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-[#0176D3]" />
            <h1 className="text-xl font-bold text-[#181818]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Event 360
            </h1>
          </div>
          <p className="text-xs text-[#706E6B]">{data.event_number} — {data.event_name}</p>
        </div>
        <button onClick={() => { load(); loadCopilot(); }} className="p-2 text-[#706E6B] hover:text-[#181818] border border-[#C9C9C9] rounded-lg" data-testid="360-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* ── Event identity strip ─────────────────────────────────────── */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl p-4" data-testid="eo03-event-header">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-bold text-[#181818]">{data.event_name}</h2>
            <div className="flex flex-wrap items-center gap-3 mt-1.5">
              {data.event_date && (
                <span className="flex items-center gap-1 text-xs text-[#706E6B]">
                  <Calendar className="w-3 h-3" />
                  {data.event_date}
                  {days !== null && (
                    <span className={`ml-1 font-medium ${days < 0 ? "text-[#BA0517]" : days <= 30 ? "text-[#8C4B00]" : "text-[#2E844A]"}`}>
                      ({days < 0 ? `${Math.abs(days)}d ago` : days === 0 ? "today" : `${days}d`})
                    </span>
                  )}
                </span>
              )}
              {data.location_city && (
                <span className="flex items-center gap-1 text-xs text-[#706E6B]">
                  <MapPin className="w-3 h-3" /> {data.location_city}
                </span>
              )}
              {data.guest_count && (
                <span className="flex items-center gap-1 text-xs text-[#706E6B]">
                  <Users className="w-3 h-3" /> {data.guest_count} guests
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${rs.bg} ${rs.text}`}>
              {readiness.overall_state?.replace(/_/g, " ") || "—"}
            </span>
            <span className="text-[11px] text-[#706E6B]">
              {data.planning_stage?.replace(/_/g, " ")}
            </span>
          </div>
        </div>
      </div>

      {/* ── Counter strip ────────────────────────────────────────────── */}
      <CounterStrip data={data} />

      {/* ── Main three-section layout ────────────────────────────────── */}
      <div className="space-y-4">
        <NeedsAttention data={data} navigate={navigate} />
        <EventFlow data={data} navigate={navigate} />
        <ActNow data={data} copilot={copilot} loadingCopilot={loadingCopilot} navigate={navigate} />
      </div>

      {/* ── Thread health grid ───────────────────────────────────────── */}
      <ThreadGrid threads={data.threads} eventId={eventId} navigate={navigate} />

    </div>
  );
}
